import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/auth/profile');
        setProfile(res.data);
      } catch (e) {
        setMessage({ type: 'error', text: 'Failed to load profile' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill all password fields' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New password and confirm password do not match' });
      return;
    }

    setSaving(true);
    try {
      await axios.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await refreshUser();
      setMessage({ type: 'success', text: 'Password changed successfully' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg">
          <Typography>Loading profile...</Typography>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4">My Profile</Typography>
        </Box>

        {message && (
          <Alert sx={{ mb: 2 }} severity={message.type} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>User Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First Name" value={profile?.firstName || user?.firstName || ''} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Name" value={profile?.lastName || user?.lastName || ''} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={profile?.email || user?.email || ''} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Role" value={profile?.role?.name || user?.role?.name || ''} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>
        </Paper>

        {String(profile?.role?.code || user?.role?.code || '').toUpperCase() === 'ADMIN' && profile?.school && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>School Details (Read Only)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="School Name" value={profile.school.name || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="School Code" value={profile.school.code || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Billing Name" value={profile.school.billingName || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Branch Name" value={profile.school.branchName || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={profile.school.email || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={profile.school.phone || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="City" value={profile.school.city || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="State" value={profile.school.state || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Address" value={profile.school.address || ''} InputProps={{ readOnly: true }} /></Grid>
            </Grid>
          </Paper>
        )}

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Change Password</Typography>
          <Box component="form" onSubmit={handleChangePassword}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="password"
                  label="Current Password"
                  fullWidth
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="password"
                  label="New Password"
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="password"
                  label="Confirm New Password"
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Update Password'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default Profile;